import * as Tone from 'tone';
import type { Beat, KitId, MusicEvent, Seconds, SectionName } from '../core/types';
import { createArrangement, type Arrangement } from './arrangement';
import { createBand, type Band } from './band';
import { BEATS_PER_BAR, beatToSeconds } from './plan';
import { preferPlayback } from './session';

const START_DELAY = 0.2;
const MUSIC_AHEAD_BARS = 1;
const BAR_HISTORY = 3;
const INTENSITY_STEP = 0.02;
const KICK_DECAY_SECONDS = 0.16;
const HEARD_KEPT = 64;
const HEARD_RESOLUTION = 1000;

export interface RunHead {
  readonly bpm: number;
  readonly kit: KitId;
  readonly seed: string;
  readonly marks?: readonly Beat[];
}

export interface AudioEngine {
  ready(): Promise<void>;
  unlock(): void;
  startRun(head: RunHead): Seconds;
  ensureMusic(beat: Beat): void;
  sectionAt(beat: Beat): SectionName | null;
  kickPulse(at: Seconds): number;
  heardAt(beat: Beat): Seconds | null;
  now(): Seconds;
  stop(when?: Seconds): void;
  dispose(): void;
  readonly transport: ReturnType<typeof Tone.getTransport>;
}

export function createAudioEngine(): AudioEngine {
  preferPlayback();
  const limiter = new Tone.Limiter(-1).toDestination();
  const master = new Tone.Gain(0.82).connect(limiter);
  const glue = new Tone.Compressor({ threshold: -20, ratio: 3, attack: 0.006, release: 0.16 }).connect(
    master,
  );
  const reverb = new Tone.Reverb({ decay: 2.4, preDelay: 0.012, wet: 1 }).connect(master);
  const send = new Tone.Gain(0.16).connect(reverb);
  const transport = Tone.getTransport();

  let lastKick = -1;
  let runStart = 0;
  let runBpm = 120;
  let band: Band | null = null;
  let bandKit: KitId | null = null;
  let arrangement: Arrangement | null = null;
  let scheduledBar = 0;
  let intensity = 0;
  const slots = new Map<number, number[]>();
  const heard = new Map<number, Seconds>();
  const beatKey = (beat: Beat): number => Math.round(beat * HEARD_RESOLUTION);

  function hear(beat: Beat, time: Seconds): void {
    const key = beatKey(beat);
    if (heard.has(key)) return;
    heard.set(key, time);
    for (const old of heard.keys()) {
      if (heard.size <= HEARD_KEPT) break;
      heard.delete(old);
    }
  }

  function ensureBand(kit: KitId): Band {
    if (band && bandKit === kit) return band;
    band?.dispose();
    band = createBand(kit, glue);
    band.output.connect(send);
    bandKit = kit;
    return band;
  }

  function scheduleBar(bar: number): void {
    if (!arrangement || !band) return;
    const voices = band;
    const ids = arrangement.eventsInBars(bar, bar + 1).map((event: MusicEvent) =>
      transport.schedule((time) => {
        if (event.lane === 'kick') lastKick = time;
        hear(event.beat, time);
        voices.play(event, time, runBpm);
      }, beatToSeconds(event.beat, runBpm)),
    );
    slots.set(bar, ids);
    for (const key of slots.keys()) if (key < bar - BAR_HISTORY) slots.delete(key);
  }

  function scheduleMusic(throughBeat: Beat): void {
    if (!arrangement) return;
    const throughBar = Math.min(Math.floor(throughBeat / BEATS_PER_BAR) + 1, arrangement.endBar);
    while (scheduledBar < throughBar) {
      scheduleBar(scheduledBar);
      scheduledBar++;
    }
  }

  function clearSchedule(): void {
    transport.cancel(0);
    slots.clear();
    heard.clear();
    scheduledBar = 0;
    lastKick = -1;
  }

  return {
    async ready() {
      await Tone.start();
    },
    unlock() {
      const state: string = Tone.getContext().state;
      if (state !== 'running' && state !== 'closed') void Tone.start().catch(() => undefined);
    },
    startRun(head) {
      transport.stop();
      clearSchedule();
      transport.position = 0;
      transport.bpm.value = head.bpm;
      runBpm = head.bpm;
      runStart = Tone.now() + START_DELAY;
      arrangement = createArrangement(head.seed, head.kit, undefined, head.marks);
      intensity = arrangement.intensityAt(0);
      ensureBand(head.kit).setIntensity(intensity, runStart);
      scheduleMusic(BEATS_PER_BAR * MUSIC_AHEAD_BARS);
      transport.start(runStart);
      return runStart;
    },
    ensureMusic(beat) {
      if (!arrangement) return;
      scheduleMusic(beat + BEATS_PER_BAR);
      const next = arrangement.intensityAt(Math.max(0, beat));
      if (Math.abs(next - intensity) >= INTENSITY_STEP) {
        intensity = next;
        band?.setIntensity(intensity);
      }
    },
    sectionAt(beat) {
      if (!arrangement || beat < 0) return null;
      return arrangement.sectionAt(Math.floor(beat / BEATS_PER_BAR)).name;
    },
    kickPulse(at) {
      if (lastKick < 0 || at < lastKick) return 0;
      return Math.exp(-(at - lastKick) / KICK_DECAY_SECONDS);
    },
    heardAt(beat) {
      return heard.get(beatKey(beat)) ?? null;
    },
    now() {
      return Tone.now();
    },
    stop(when) {
      const time = when ?? Tone.now();
      transport.stop(time);
      band?.stop(time);
      clearSchedule();
    },
    dispose() {
      transport.stop();
      transport.cancel(0);
      band?.dispose();
      band = null;
      send.dispose();
      reverb.dispose();
      glue.dispose();
      master.dispose();
      limiter.dispose();
    },
    transport,
  };
}
